import { useState, useEffect, useMemo } from 'react';
import { Badge, Button, Card, DateInput, TextButton } from '@safetyhub/ui';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { useModalStore } from '../../stores/modal';
import { extractApiError } from '../../lib/api';
import { createInspection, Inspection, getInspections, InspectionListItem } from '../../lib/inspections';
import { sitesApi, Site, organizationsApi, isSiteSelectable } from '../../lib/organizations';
import Modal from '../Modal';
import { ConfirmDialog } from '../inspection/shared/ConfirmDialog';
import { ParticipantSelector } from '../inspection/ParticipantSelector';
import { useToastStore } from '../../stores/toast';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { useParticipantsState } from '../../hooks/useParticipantsState';
import { ChevronLeftIcon } from '../icons';
import { MemberSelect } from '../MemberSelect';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'create-inspection-form';

interface CreateInspectionModalProps {
  /** 생성 성공 후 호출되는 콜백 */
  onSuccess?: (inspection: Inspection) => void;
}

export default function CreateInspectionModal({ onSuccess }: CreateInspectionModalProps) {
  const { user, currentMembership } = useAuthStore();
  const {
    createInspection: isOpen,
    createInspectionDefaultDate,
    closeCreateInspection,
  } = useModalStore();
  const navigate = useNavigate();
  const companyId = currentMembership?.companyId;
  const layout = useDeviceLayout();

  // 중복 점검 다이얼로그 상태
  const [duplicateInspection, setDuplicateInspection] = useState<InspectionListItem | null>(null);

  // 모바일 상태
  const [siteSheetOpen, setSiteSheetOpen] = useState(false);

  // 폼 상태
  const [siteId, setSiteId] = useState('');
  const { canManageInspections, can, isOwner: isOrgOwner } = usePermissions(siteId || undefined);
  // 참가자 지정·대리 예약 어포던스(P4 ②) = 소유자 성질 ∥ `inspection.manage`(조직 수준 — 현장은 아직 고르기 전일 수 있다).
  // 옛 `role === 'OWNER'`. 서버 create는 요청자 키(inspection.create)·대상 배정만 보고 요청자 역할은 묻지 않는다.
  const isOwner = isOrgOwner || can('inspection.manage');
  const [notes, setNotes] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  // 담당자 추가 모드: 'list' | 'addMember' | 'inviteExternal'
  const [assignMode, setAssignMode] = useState<'list' | 'addMember' | 'inviteExternal'>('list');
  const [addMemberId, setAddMemberId] = useState('');
  const [inviteIdentifier, setInviteIdentifier] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // UI 상태
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const { showToast } = useToastStore();

  // 참가자 상태 훅 (Owner 전용, 현장 선택 시 활성화)
  const participantsHook = useParticipantsState({
    companyId,
    siteId: siteId || undefined,
    initialSource: { kind: 'previous-inspection' },
    enabled: isOwner && isOpen && !!siteId,
  });
  const { participants, unassignedMembers, members } = participantsHook;

  // 선택한 현장 객체
  const selectedSite = useMemo(() => sites.find((s) => s.id === siteId), [siteId, sites]);

  // 모달 열릴 때 현장 목록 로드
  useEffect(() => {
    if (isOpen && companyId) {
      loadSites();
      resetForm(createInspectionDefaultDate);
    }
  }, [isOpen, companyId]);

  // 현장 변경 시 assignMode 초기화
  useEffect(() => {
    setAssignMode('list');
    setAddMemberId('');
    setInviteIdentifier('');
    setInvitePhone('');
  }, [siteId]);

  const resetForm = (defaultDate?: string | null) => {
    setSiteId('');
    setNotes('');
    setScheduledAt(defaultDate ?? '');
    setAssignMode('list');
    setAddMemberId('');
    setInviteIdentifier('');
    setInvitePhone('');
    setSiteSheetOpen(false);
    setDuplicateInspection(null);
  };

  // 현장 + 점검일 모두 선택 시 중복 점검 체크
  useEffect(() => {
    if (!siteId || !scheduledAt || !companyId) return;

    let cancelled = false;
    getInspections(companyId, { siteId, scheduledDate: scheduledAt, limit: 5 })
      .then((res) => {
        if (cancelled) return;
        const found = res.data?.find((insp) => !insp.deletedAt);
        setDuplicateInspection(found ?? null);
      })
      .catch(() => {
        // 체크 실패는 조용히 무시 (생성 진행 허용)
      });

    return () => { cancelled = true; };
  }, [siteId, scheduledAt, companyId]);

  const loadSites = async () => {
    if (!companyId) return;

    setIsSitesLoading(true);
    try {
      const data = await sitesApi.findAll(companyId);
      // 일시중지·종료 현장은 선택지에서 뺀다(이관중은 노출 후 disabled — 아래 목록 렌더 참조)
      let activeSites = data.filter(isSiteSelectable);
      // 비소유자는 할당된 현장에서만 점검 생성 가능(scope — 서버 create의 「소유자=전 현장 / 그 외=배정 현장」과 같은 축, P4 ②)
      if (!isOrgOwner && user) {
        activeSites = activeSites.filter((site) =>
          site.assignments?.some((a) => a.member?.member?.userId === user.id),
        );
      }
      setSites(activeSites);
    } catch {
      showToast('현장 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsSitesLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    closeCreateInspection();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;

    if (!siteId) {
      showToast('현장을 선택해주세요.', 'error');
      return;
    }

    if (!scheduledAt) {
      showToast('점검일을 선택해주세요.', 'error');
      return;
    }

    if (!canManageInspections) {
      showToast('선택한 현장에 대한 점검 관리 권한이 없습니다.', 'error');
      return;
    }

    const payload = participantsHook.getPayload();

    // Owner가 참가자를 한 명도 선택하지 않은 경우
    if (isOwner && payload.participantUserIds.length === 0) {
      showToast('최소 1명의 참가자를 선택해주세요.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await createInspection({
        companyId,
        siteId,
        notes: notes || undefined,
        scheduledAt: scheduledAt || undefined,
        inspectorId: payload.primaryParticipantUserId || undefined,
        participantUserIds: isOwner ? payload.participantUserIds : undefined,
        primaryParticipantUserId: payload.primaryParticipantUserId,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || '점검 생성에 실패했습니다.');
      }

      const inspection = response.data;

      onSuccess?.(inspection);
      handleClose();
      showToast('점검이 예약되었습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSite = () => {
    closeCreateInspection();
    // 현재 모달 close가 React에 완전히 반영된 후 새 모달 open — 같은 tick에서 두 modal 전환 시
    // Modal body.overflow cleanup이 신규 open을 덮어써서 신규 모달이 마운트 안 되는 문제 회피
    // 'inspection' source로 열어 저장 후 CreateInspectionModal 자동 재오픈
    setTimeout(() => {
      useModalStore.getState().openCreateSiteForInspection();
    }, 0);
  };

  // 조직 멤버를 현장에 배정하고 참가자로 추가
  const handleAddMemberToSite = async () => {
    if (!companyId || !siteId || !addMemberId) return;
    setIsAddingMember(true);
    try {
      await sitesApi.assignMember(companyId, siteId, { memberId: addMemberId });
      await loadSites();
      await participantsHook.refetchSite();

      const addedMember = members.find((m) => m.id === addMemberId);
      if (addedMember) {
        participantsHook.addParticipant(addedMember);
      }

      setAssignMode('list');
      setAddMemberId('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsAddingMember(false);
    }
  };

  // 외부 사용자 초대
  const handleInviteExternal = async () => {
    if (!companyId || !siteId) return;
    if (!inviteIdentifier.trim() && !invitePhone.trim()) {
      showToast('이메일/아이디 또는 전화번호를 입력해주세요.', 'error');
      return;
    }
    setIsInviting(true);
    try {
      const result = await organizationsApi.inviteMember(companyId, {
        identifier: inviteIdentifier.trim() || undefined,
        phone: invitePhone.trim() || undefined,
        // P7-b-2: 초대의 역할 축은 roleKeys 하나다. 이 자리는 「현장 참가자로 합류」라 역할을 고르는 UI가
        // 없으므로 비워 서버 기본값(`member`)을 받는다 — 옛 `role: 'SAFETY_MANAGER'` 하드코딩의 대체.
        siteId,
      });

      if (result.type === 'MEMBER_ADDED' && result.member) {
        await loadSites();
        await participantsHook.refetchSite();
        participantsHook.addParticipant(result.member);
      } else {
        showToast('초대가 발송되었습니다.', 'success');
      }

      setAssignMode('list');
      setInviteIdentifier('');
      setInvitePhone('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const isMobile = layout !== 'desktop';

  const checkedCount = participantsHook.checkedCount;

  // Owner 필수 검증: 참가자도 선택해야 제출 가능
  const isSubmitDisabled =
    isLoading || !siteId || !scheduledAt || (isOwner && checkedCount === 0) || !!(siteId && !canManageInspections) || !!duplicateInspection;

  const getDisabledTooltip = () => {
    if (!siteId) return '현장을 선택해주세요';
    if (siteId && !canManageInspections) return '선택한 현장에 대한 점검 관리 권한이 없습니다';
    if (!scheduledAt) return '점검일을 선택해주세요';
    if (isOwner && checkedCount === 0) return '참가자를 선택해주세요';
    return '';
  };

  const handleDuplicateNavigate = () => {
    if (!duplicateInspection) return;
    closeCreateInspection();
    navigate(`/inspections/${duplicateInspection.id}`);
  };

  const handleDuplicateCancel = () => {
    setSiteId('');
    setScheduledAt('');
    setDuplicateInspection(null);
  };

  // selectedSite는 참조용 (향후 확장) — 현재 UI에서는 사용하지 않지만 assignments 로드 트리거용
  void selectedSite;

  return (
    <>
      <ConfirmDialog
        isOpen={!!duplicateInspection}
        onClose={handleDuplicateCancel}
        onConfirm={handleDuplicateNavigate}
        title="중복 점검 확인"
        message="해당 현장에 이 날짜의 점검이 이미 존재합니다. 기존 점검으로 이동하시겠습니까?"
        confirmText="해당 점검으로 이동"
        cancelText="취소"
        confirmStyle="primary"
      />
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="새 점검 예약"
        description={isMobile ? undefined : '현장을 선택하고 점검일을 지정하여 점검을 예약합니다.'}
        size={isMobile ? 'md' : 'xl'}
        footer={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              취소
            </Button>
            <div className="relative group">
              {/* 푸터는 base가 본문과 **형제**로 렌더하므로 아래 `<form>` 밖이다 — `form` 속성으로
                  이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다). */}
              <Button variant="primary" type="submit" form={FORM_ID} disabled={isSubmitDisabled}>
                {isLoading ? '예약 중...' : '점검 예약'}
              </Button>
              {!isLoading && isSubmitDisabled && (
                <>
                  {/* ⚠️ 말풍선을 버튼 «가운데»에 걸지 말 것 — 이 버튼은 푸터 오른쪽 끝이고, 모달
                      패널이 `bodyScroll`의 `overflow-hidden`(Modal.tsx → base `Modal`)이라 **가로도
                      잘린다**(CSS에서 한 축이 `visible`이 아니면 다른 축도 `visible`일 수 없다).
                      기제의 이름은 종전 `panelClassName="max-h-[90vh] overflow-y-auto"`에서
                      바뀠지만 **클리핑한다는 사실은 그대로다**. 종전 `left-1/2 -translate-x-1/2`
                      + `whitespace-nowrap`은 긴 문구에서 오른쪽이 통째로 잘렸다(2026-10-06 prod 모바일
                      제보). 오른쪽에 붙이고 줄바꿈을 허용하되 `w-max`로 짧은 문구는 한 줄을 유지한다. */}
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 w-max max-w-[min(20rem,calc(100vw-2.5rem))] text-xs text-white text-center bg-ink-800 rounded-card-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {getDisabledTooltip()}
                  </div>
                  {/* 꼬리는 말풍선이 아니라 «버튼» 가운데를 가리켜야 하므로 래퍼의 자식으로 둔다
                      (말풍선 안에 두면 오른쪽 정렬된 말풍선의 가운데를 가리켜 버튼과 어긋난다). */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink-800 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </>
              )}
            </div>
          </>
        }
      >
        <form id={FORM_ID} onSubmit={handleSubmit}>
          {/* 현장 선택 */}
          <div className={isMobile ? 'mb-4' : 'mb-6'}>
            <label htmlFor="inspection-site" className="block text-sm font-medium text-ink-700">
              현장 선택 <span className="text-red-500">*</span>
            </label>
            {isSitesLoading ? (
              <div className="mt-1 flex items-center text-ink-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600 mr-2"></div>
                현장 목록 불러오는 중...
              </div>
            ) : sites.length === 0 ? (
              <Card
                surface="soft"
                elevation="none"
                padding="none"
                className="mt-1 text-center py-4"
              >
                <p className="text-ink-500 text-sm">등록된 현장이 없습니다.</p>
                <TextButton onClick={handleAddSite} className="mt-2 text-sm">
                  현장 추가하기
                </TextButton>
              </Card>
            ) : isMobile ? (
              /* 모바일: 인라인 커스텀 드롭다운 */
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={() => !isLoading && setSiteSheetOpen((v) => !v)}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between px-3 py-2 border rounded-control shadow-sm bg-white text-left disabled:bg-ink-100 ${
                    siteSheetOpen ? 'border-brand-500 ring-1 ring-brand-500' : 'border-ink-300'
                  }`}
                >
                  <span className={siteId ? 'text-ink-900' : 'text-ink-500'}>
                    {siteId
                      ? sites.find((s) => s.id === siteId)?.name ?? '현장을 선택하세요'
                      : '현장을 선택하세요'}
                  </span>
                  <ChevronDownIcon
                    className={`h-4 w-4 text-ink-400 shrink-0 transition-transform ${siteSheetOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {siteSheetOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setSiteSheetOpen(false)} />
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-ink-200 rounded-card shadow-lg z-20 max-h-48 overflow-y-auto">
                      {sites.map((site) => {
                        const isPending = site.status === 'TRANSFER_PENDING';
                        return (
                          <button
                            key={site.id}
                            type="button"
                            disabled={isPending}
                            onClick={() => {
                              if (isPending) return;
                              setSiteId(site.id);
                              setSiteSheetOpen(false);
                            }}
                            title={isPending ? '이관 대기 중인 현장입니다.' : undefined}
                            className={`w-full px-3 py-2.5 text-left text-sm border-b border-ink-100 last:border-b-0 ${
                              isPending
                                ? 'text-ink-300 cursor-not-allowed'
                                : siteId === site.id
                                  ? 'text-brand-600 font-medium bg-brand-50 active:bg-brand-50'
                                  : 'text-ink-900 active:bg-ink-100'
                            }`}
                          >
                            <span className="flex items-center gap-1.5">
                              {site.name}
                              {isPending && (
                                <Badge tone="amber" shape="rounded">
                                  이관대기
                                </Badge>
                              )}
                            </span>
                            {site.address && (
                              <span className="block text-xs text-ink-500 mt-0.5">
                                {site.address}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* PC: 네이티브 select */
              <select
                id="inspection-site"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                disabled={isLoading}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
              >
                <option value="">현장을 선택하세요</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id} disabled={site.status === 'TRANSFER_PENDING'}>
                    {site.name}
                    {site.status === 'TRANSFER_PENDING' ? ' (이관대기)' : site.address ? ` - ${site.address}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 참가자 선택 (Owner만 표시) */}
          {isOwner && (
            <div className={isMobile ? 'mb-4' : 'mb-6'}>
              <label className="block text-sm font-medium text-ink-700">
                참가자 <span className="text-red-500">*</span>
              </label>
              {!siteId ? (
                <p className="mt-1 text-sm text-ink-400">현장을 먼저 선택해주세요</p>
              ) : assignMode === 'list' ? (
                <div className="mt-1">
                  <ParticipantSelector
                    participants={participants}
                    onToggle={participantsHook.toggleMember}
                    onSetPrimary={participantsHook.setPrimary}
                    disabled={isLoading}
                    onAddMember={() => setAssignMode('addMember')}
                  />
                </div>
              ) : assignMode === 'addMember' ? (
                /* 조직 멤버 추가 모드 */
                <div className="mt-1 border border-ink-200 rounded-card p-3">
                  <TextButton
                    tone="neutral"
                    onClick={() => { setAssignMode('list'); setAddMemberId(''); }}
                    className="flex items-center gap-1 text-sm mb-3"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    <span>뒤로</span>
                  </TextButton>
                  {/* 세그먼티드 토글(현재 모드 표시) — Button 갈래가 아니라 그대로 둔다.
                      활성 칸을 disabled로 표시하는데 Button은 disabled에 opacity-50을 걸어 흐려진다. */}
                  {/* eslint-disable local/no-self-painted-button */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      className="flex-1 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-control"
                      disabled
                    >
                      조직 멤버 추가
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAssignMode('inviteExternal'); setAddMemberId(''); }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-ink-600 border border-ink-300 rounded-control hover:bg-ink-50"
                    >
                      외부 사용자 초대
                    </button>
                  </div>
                  {/* eslint-enable local/no-self-painted-button */}
                  <p className="text-sm text-ink-600 mb-2">
                    조직 멤버 중 이 현장에 미배정된 멤버:
                  </p>
                  {unassignedMembers.length === 0 ? (
                    <p className="text-sm text-ink-400 mb-2">추가 가능한 조직 멤버가 없습니다.</p>
                  ) : (
                    <div className="mb-3">
                      <MemberSelect
                        members={unassignedMembers}
                        value={addMemberId}
                        onChange={setAddMemberId}
                        placeholder="멤버를 선택하세요"
                        searchable={unassignedMembers.length > 5}
                        disabled={isAddingMember}
                      />
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddMemberToSite}
                      disabled={isAddingMember || !addMemberId}
                    >
                      {isAddingMember ? '추가 중...' : '추가'}
                    </Button>
                  </div>
                </div>
              ) : (
                /* 외부 사용자 초대 모드 */
                <div className="mt-1 border border-ink-200 rounded-card p-3">
                  <TextButton
                    tone="neutral"
                    onClick={() => setAssignMode('addMember')}
                    className="flex items-center gap-1 text-sm mb-3"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    <span>뒤로</span>
                  </TextButton>
                  {/* 세그먼티드 토글 — 위 addMember 모드와 같은 이유로 유지. */}
                  {/* eslint-disable local/no-self-painted-button */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => { setAssignMode('addMember'); setInviteIdentifier(''); setInvitePhone(''); }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-ink-600 border border-ink-300 rounded-control hover:bg-ink-50"
                    >
                      조직 멤버 추가
                    </button>
                    <button
                      type="button"
                      className="flex-1 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-control"
                      disabled
                    >
                      외부 사용자 초대
                    </button>
                  </div>
                  {/* eslint-enable local/no-self-painted-button */}
                  <p className="text-sm text-ink-500 mb-3">
                    새 멤버를 초대하면 수락 시 자동으로 이 현장에 배정됩니다.
                  </p>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-ink-600 mb-1">
                      이메일 또는 아이디
                    </label>
                    <input
                      type="text"
                      value={inviteIdentifier}
                      onChange={(e) => setInviteIdentifier(e.target.value)}
                      placeholder="user@example.com 또는 user123"
                      disabled={isInviting}
                      className="w-full px-3 py-2 text-sm border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-ink-600 mb-1">
                      전화번호 <span className="text-ink-400 font-normal">(선택)</span>
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={invitePhone}
                      onChange={(e) =>
                        setInvitePhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))
                      }
                      placeholder="01012345678"
                      maxLength={11}
                      disabled={isInviting}
                      className="w-full px-3 py-2 text-sm border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setAssignMode('list');
                        setInviteIdentifier('');
                        setInvitePhone('');
                      }}
                      disabled={isInviting}
                    >
                      취소
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleInviteExternal}
                      disabled={isInviting || (!inviteIdentifier.trim() && !invitePhone.trim())}
                    >
                      {isInviting ? '초대 중...' : '초대'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={`${isMobile ? 'space-y-4' : 'space-y-6'} max-h-[60vh] overflow-y-auto pr-2`}>
            {/* 점검일 */}
            <div>
              <label className="block text-sm font-medium text-ink-700">
                점검일 <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <DateInput
                  label="점검일"
                  placeholder="점검일을 선택하세요"
                  value={scheduledAt}
                  onChange={setScheduledAt}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </div>

            {/* 메모 */}
            <div>
              <label htmlFor="inspection-notes" className="block text-sm font-medium text-ink-700">
                메모 (선택)
              </label>
              <textarea
                id="inspection-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={isMobile ? 2 : 3}
                disabled={isLoading}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
                placeholder="점검에 대한 메모를 입력하세요..."
              />
            </div>
          </div>

        </form>
      </Modal>
    </>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}
